import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { SnackbarService } from '../../../core/services';
import { v4 as uuid } from 'uuid';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { CalendarEventExternalLinkRequest, CalendarEventExternalLinkSuccess } from '../../../store/calendar/calendar.actions';
import { take } from 'rxjs';
import { BRANDING, withBrandName } from '../../../core/config/branding';

type CallSessionData = {
  id: string;
  channelId: string | null;
  launchUrl: string;
  title: string;
  mode: 'audio' | 'video';
  createdAt: string;
  userName?: string;
  isOwner?: boolean;
};

const CALL_SESSION_STORAGE_PREFIX = 'meetrix.daily.session.';

@Component({
  selector: 'app-calendar-join-token',
  standalone: true,
  imports: [CommonModule, RouterModule, MatProgressSpinnerModule],
  templateUrl: './calendar-join-token.html',
  styleUrl: './calendar-join-token.scss',
})
export class CalendarJoinToken {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackbar = inject(SnackbarService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  protected readonly status = signal<'joining' | 'error'>('joining');
  protected readonly message = signal('Joining meeting…');
  protected readonly brand = BRANDING;

  ngOnInit(): void {
    const token = this.route.snapshot.paramMap.get('token')?.trim() ?? '';
    if (!token || typeof window === 'undefined') {
      this.fail('Invalid join link.');
      return;
    }

    this.store.dispatch(CalendarEventExternalLinkRequest({ token }));

    this.actions$.pipe(ofType(CalendarEventExternalLinkSuccess), take(1)).subscribe(({ payload }) => {
      const launchUrl = payload?.launch_url?.trim?.() ?? '';
        if (!launchUrl) {
          this.fail('Unable to generate join link.');
          return;
        }

        const sessionId = uuid();
        const sessionData: CallSessionData = {
          id: sessionId,
          channelId: null,
          launchUrl,
          title: payload?.title?.trim?.() || withBrandName('Meeting'),
          mode: 'video',
          createdAt: new Date().toISOString(),
          userName: payload?.user_name?.trim?.() || undefined,
          isOwner: Boolean(payload?.is_owner),
        };

        window.localStorage.setItem(CALL_SESSION_STORAGE_PREFIX + sessionId, JSON.stringify(sessionData));
        const basePath = `${window.location.origin}${window.location.pathname}`;
        console.log('Opening meeting room with URL:', launchUrl);
        window.location.replace(`${basePath}#/call/${sessionId}`);
    });
  }

  private isExpiredMeetingError(message: string): boolean {
    const normalized = message.trim().toLowerCase();
    return (
      normalized.includes('already ended') ||
      normalized.includes('expired') ||
      normalized.includes('ended')
    );
  }

  private fail(message: string, options?: { stayOnPage?: boolean }): void {
    this.status.set('error');
    this.message.set(message);

    if (options?.stayOnPage) {
      return;
    }

    this.snackbar.showNotification('error', '', message, 'Close');
  }
}
